/* Printora Product V27 — interaction-only controller.
   Liquid owns all visible structure. This file performs local state changes only:
   variants, gallery, size guide, personalisation and intentional dialogs. */
(() => {
  'use strict';

  const MOBILE = window.matchMedia('(max-width: 1199px)');

  const slug = (value) => String(value || '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  const normalise = (value) => String(value || '').replace(/\s+/g, ' ').trim().toLowerCase();

  function parseJson(node, fallback) {
    if (!node) return fallback;
    try { return JSON.parse(node.textContent || ''); }
    catch (_) { return fallback; }
  }

  function fields(engine) {
    return engine ? [...engine.querySelectorAll('fieldset[data-index]')] : [];
  }

  function selectedValues(engine) {
    return fields(engine).map(fieldset => fieldset.querySelector('input[type="radio"]:checked')?.value || '');
  }

  const variantIndexes = new WeakMap();
  function findVariant(variants, values) {
    let index = variantIndexes.get(variants);
    if (!index) {
      index = new Map(variants.map(variant => [JSON.stringify(variant.options.map(String)), variant]));
      variantIndexes.set(variants, index);
    }
    return index.get(JSON.stringify(values.map(String))) || null;
  }

  function labelFor(input) {
    const next = input?.nextElementSibling;
    return next?.tagName === 'LABEL' ? next : null;
  }

  function setPrice(root, productId, variant) {
    const host = root.querySelector(`#product-price-${CSS.escape(String(productId))}`);
    if (!host || !variant) return;

    const price = document.createElement('div');
    const onSale = Number(variant.compareAtPrice || 0) > Number(variant.price || 0);
    price.className = `price price--medium${onSale ? ' price--on-sale' : ''}${variant.available ? '' : ' price--sold-out'}`;

    const dl = document.createElement('dl');
    const row = document.createElement('div');
    row.className = onSale ? 'price__sale' : 'price__regular';

    const last = document.createElement('dd');
    last.className = 'price__last';
    const current = document.createElement('span');
    current.className = `price-item ${onSale ? 'price-item--sale' : 'price-item--regular'}`;
    current.textContent = variant.priceFormatted || '';
    last.appendChild(current);
    row.appendChild(last);

    if (onSale) {
      const compare = document.createElement('dd');
      compare.className = 'price__compare';
      const s = document.createElement('s');
      s.className = 'price-item price-item--regular';
      s.textContent = variant.compareAtFormatted || '';
      compare.appendChild(s);
      row.appendChild(compare);
    }

    dl.appendChild(row);
    price.appendChild(dl);
    host.replaceChildren(price);
  }

  function setVariantInputs(productId, variant) {
    [`product-form-${productId}`, `product-form-installment-${productId}`].forEach(id => {
      const form = document.getElementById(id);
      if (!form) return;
      form.querySelectorAll('input[name="id"]').forEach(input => {
        const nextValue = variant ? String(variant.id) : '';
        if (input.value === nextValue) return;
        input.value = nextValue;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }

  function setAddButton(root, productId, variant) {
    const form = document.getElementById(`product-form-${productId}`);
    const button = form?.querySelector('[name="add"]') || root.querySelector('#product-add-to-cart');
    if (!button) return;

    const strings = window.variantStrings || {};
    if (!variant) {
      button.disabled = true;
      button.textContent = strings.unavailable || 'Unavailable';
      button.dataset.inventoryQuantity = '0';
      button.dataset.available = 'false';
      return;
    }

    const quantity = Number(variant.inventoryQuantity || 0);
    button.dataset.inventoryQuantity = String(quantity);

    if (!variant.available) {
      button.disabled = true;
      button.textContent = strings.soldOut || 'Sold out';
      button.dataset.available = 'false';
      return;
    }

    button.disabled = false;
    const preorder = Boolean(variant.inventoryManagement && quantity <= 0 && variant.inventoryPolicy === 'continue');
    button.textContent = preorder ? (strings.preOrder || 'Pre-order') : (strings.addToCart || 'Add to cart');
    button.dataset.available = preorder ? 'true' : 'false';
  }

  function setStock(root, variant) {
    const stock = root.querySelector('[data-pv2-stock]');
    if (!stock) return;
    if (!variant) {
      stock.textContent = 'Unavailable';
      return;
    }
    if (variant.available) {
      stock.innerHTML = '<span class="pv2-stock-dot"></span>In stock · Dispatches in 2–3 business days';
    } else {
      stock.textContent = 'Sold out in this size and colour — try another size or colour';
    }
  }

  function setFabric(root, colourValue) {
    const node = root.querySelector('[data-pv2-fabric-composition]');
    if (!node) return;
    node.textContent = normalise(colourValue).includes('heather')
      ? 'Heather Grey: 90% cotton, 10% viscose'
      : 'Fabric: 100% cotton';
  }

  /* Garment weight key embedded in the Fabric option value (240 / 180 / 150 / 280). Never shown to customers. */
  function fabricKeyOf(value) {
    const v = normalise(value);
    if (v.includes('240')) return '240';
    if (v.includes('180')) return '180';
    if (v.includes('150') || v.includes('kids')) return '150';
    if (v.includes('280')) return '280';
    return '';
  }

  const FABRIC_FEEL = {
    '240': '240gsm · thicker, structured premium weight',
    '180': '180gsm · soft, comfortable everyday weight',
    '150': '150gsm · soft, lightweight kids tee',
    '280': '55% polyester / 45% cotton · heavyweight fleece'
  };

  function setFabricWeight(root, fabricValue) {
    const node = root.querySelector('[data-pv2-fabric-weight]');
    if (!node) return;
    node.textContent = FABRIC_FEEL[fabricKeyOf(fabricValue)] || '100% cotton';
  }

  function setKidsDetails(root, variant) {
    const panel = root.querySelector('[data-v27-kids-details]');
    const host = root.querySelector('[data-v27-options]');
    if (!panel || !host) return;
    const values = (variant?.options || selectedValues(root.querySelector('[data-v27-variant-engine]'))).map(normalise);
    const isKids = values.some(value => /kids|children|\d{1,2}\s*[-–/]\s*\d{1,2}\s*(years?|yrs?)/.test(value));
    const kindOf = text => /hoodie|hooded/.test(text) ? 'hoodie'
      : /jumper|sweatshirt/.test(text) ? 'jumper'
      : /t-shirt|tshirt|\btee\b/.test(text) ? 'tee' : '';
    const specific = values.map(kindOf).find(Boolean);
    const kind = specific || kindOf(normalise(host.dataset.v27ProductType)) || kindOf(normalise(host.dataset.v27ProductTitle));
    panel.hidden = !isKids || !kind;
    if (panel.hidden) return;
    const colour = normalise(root.querySelector('[data-v27-colour-input]:checked')?.value);
    const fleece = kind !== 'tee';
    let composition = fleece ? '80% ringspun cotton, 20% polyester' : '100% cotton';
    if (colour === 'heather grey') composition = fleece ? '75% ringspun cotton, 25% polyester' : '90% cotton, 10% viscose';
    if (fleece && colour === 'charcoal') composition = '52% ringspun cotton, 48% polyester';
    if (kind === 'hoodie' && colour === 'black smoke') composition = '70% ringspun cotton, 30% polyester';
    const weight = fleece ? '280gsm' : '150gsm';
    const name = kind === 'tee' ? 'Kids T-shirt' : kind === 'hoodie' ? 'Kids hoodie' : 'Kids jumper';
    panel.textContent = `${name} · ${weight} · ${composition}`;
    const fabricNode = root.querySelector('[data-pv2-fabric-composition]');
    if (fabricNode) fabricNode.textContent = composition;
    const weightNode = root.querySelector('[data-pv2-fabric-weight]');
    if (weightNode) weightNode.textContent = `${weight} · ${fleece ? 'soft cotton-rich fleece' : 'soft, lightweight cotton'}`;
    if (fleece) {
      const activeChoice = root.querySelector('[data-v27-fabric-choice].is-active');
      const title = activeChoice?.querySelector('.pv2-fabric-choice__title');
      if (title) title.textContent = `${name} · ${weight}`;
      const heading = root.querySelector('[data-v27-fabric-name]');
      if (heading) heading.textContent = `${name} · ${weight}`;
    }
  }

  function syncFabricVisual(root) {
    const picker = root.querySelector('[data-v27-fabric-picker]');
    if (!picker) return;
    const checked = root.querySelector('[data-v27-fabric-input]:checked');
    const value = checked ? checked.value : '';
    const name = picker.querySelector('[data-v27-fabric-name]');
    let title = '';
    picker.querySelectorAll('[data-v27-fabric-choice]').forEach(button => {
      const active = button.dataset.v27FabricValue === value;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
      if (active) title = button.dataset.v27FabricTitle || button.querySelector('.pv2-fabric-choice__title')?.textContent || '';
    });
    if (name && value) name.textContent = title || value;
    setFabricWeight(root, value);
  }

  function syncAudienceVisual(root) {
    const selector = root.querySelector('[data-pv2-audience-selector]');
    if (!selector) return;
    const checked = root.querySelector('[data-v27-fabric-input]:checked');
    const audience = checked ? (checked.dataset.pv2Audience || 'adults') : 'adults';
    selector.querySelectorAll('[data-pv2-audience]').forEach(button => {
      const active = (button.dataset.pv2Audience || 'adults') === audience;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
    const name = selector.querySelector('[data-pv2-audience-name]');
    if (name) name.textContent = audience === 'kids' ? 'Kids' : 'Adults';
    root.querySelectorAll('[data-v27-fabric-choice]').forEach(choice => {
      const show = (choice.dataset.pv2Audience || 'adults') === audience;
      choice.hidden = !show;
      choice.classList.toggle('pv2-option-hidden', !show);
    });
  }

  /* Hide colours and sizes the selected garment is not made in (supplier spec + real variants), and
     move the selection onto a valid choice when the current one disappears. */
  function applyGarmentFilter(root) {
    const fabricInput = root.querySelector('[data-v27-fabric-input]:checked');
    if (!fabricInput) return { colourChanged: false, sizeChanged: false };
    const key = fabricInput.dataset.v27FabricKey || fabricKeyOf(fabricInput.value);
    const probe = key ? `|${key}|` : '';
    const result = { colourChanged: false, sizeChanged: false };

    const cards = [...root.querySelectorAll('[data-v27-colour-picker] .pv2-colour-card')];
    let visible = 0;
    cards.forEach(card => {
      const spec = card.dataset.v27ColourFabrics;
      const ok = !probe || spec === undefined || spec.includes(probe);
      card.hidden = !ok;
      card.classList.toggle('pv2-option-hidden', !ok);
      if (ok) {
        visible += 1;
        card.classList.toggle('is-extra', visible > 5);
        card.classList.toggle('is-extra-m', visible > 4);
      } else {
        card.classList.remove('is-extra', 'is-extra-m');
      }
    });
    const picker = root.querySelector('[data-v27-colour-picker]');
    if (picker && cards.length) picker.dataset.v27ColourCount = String(visible);
    const more = root.querySelector('[data-v27-colour-more]');
    if (more) more.hidden = visible <= 4;

    const colourInputs = [...root.querySelectorAll('[data-v27-colour-input]')];
    const checkedColour = colourInputs.find(input => input.checked);
    if (checkedColour && cards.length) {
      const current = cards.find(card => card.dataset.v27Colour === checkedColour.value);
      if (current && current.hidden) {
        const first = cards.find(card => !card.hidden);
        const input = first ? colourInputs.find(item => item.value === first.dataset.v27Colour) : null;
        if (input) {
          input.checked = true;
          result.colourChanged = true;
        }
      }
    }
    const activeCard = cards.find(card => card.classList.contains('is-active') && !card.hidden);
    if (more && activeCard && activeCard.classList.contains('is-extra')) more.open = true;

    const sizeField = root.querySelector('[data-v27-size-fieldset]');
    if (sizeField) {
      const labels = [...sizeField.querySelectorAll('[data-v27-size-label]')].filter(label => label.dataset.v27SizeFabrics !== undefined);
      labels.forEach(label => {
        const spec = label.dataset.v27SizeFabrics || '|';
        const ok = !probe || spec.includes(probe);
        label.hidden = !ok;
        label.classList.toggle('pv2-option-hidden', !ok);
      });
      const checkedSize = sizeField.querySelector('input[type="radio"]:checked');
      const checkedLabel = checkedSize ? labelFor(checkedSize) : null;
      if (checkedLabel && checkedLabel.hidden) {
        const wanted = checkedLabel.textContent.trim();
        const candidates = labels.filter(label => !label.hidden);
        const pick = candidates.find(label => label.textContent.trim() === wanted && !label.classList.contains('unavailable'))
          || candidates.find(label => !label.classList.contains('unavailable') && !label.classList.contains('soldout'))
          || candidates[0];
        const input = pick?.htmlFor ? document.getElementById(pick.htmlFor) : null;
        if (input) {
          input.checked = true;
          result.sizeChanged = true;
        }
      }
    }
    return result;
  }

  /* Shopify returns option values in the order they were created (often alphabetical). Show sizes
     smallest to largest instead: XS…5XL first, then age ranges by starting age. */
  function sizeRank(text) {
    const t = normalise(text).replace(/–|—/g, '-');
    const letters = { xs: 10, xxs: 5, s: 20, small: 20, m: 30, medium: 30, l: 40, large: 40, xl: 50, '2xl': 60, xxl: 60, '3xl': 70, xxxl: 70, '4xl': 80, '5xl': 90 };
    if (letters[t] !== undefined) return letters[t];
    const age = t.match(/^(\d{1,2})\s*-\s*\d{1,2}/);
    if (age) return 100 + Number(age[1]);
    return 500;
  }

  function sortSizeGrid(root) {
    root.querySelectorAll('[data-v27-size-fieldset] .pv2-size-grid').forEach(grid => {
      const inputs = [...grid.querySelectorAll('input[type="radio"]')];
      if (inputs.length < 2) return;
      const pairs = inputs.map(input => ({ input, label: labelFor(input), rank: sizeRank(labelFor(input)?.textContent || input.value) }));
      if (pairs.some(pair => !pair.label)) return;
      const sorted = pairs.slice().sort((a, b) => a.rank - b.rank);
      if (sorted.every((pair, index) => pair === pairs[index])) return;
      sorted.forEach(pair => { grid.appendChild(pair.input); grid.appendChild(pair.label); });
    });
  }

  function updateOptionHeadings(engine) {
    fields(engine).forEach(fieldset => {
      const checked = fieldset.querySelector('input[type="radio"]:checked');
      const heading = fieldset.querySelector('[data-header-option]');
      if (checked && heading) heading.textContent = checked.value;
    });
  }

  function updateOptionStatuses(engine, variants) {
    const current = selectedValues(engine);
    const optionFields = fields(engine);

    optionFields.forEach((fieldset, optionIndex) => {
      fieldset.querySelectorAll('input[type="radio"]').forEach(input => {
        const candidate = current.slice();
        candidate[optionIndex] = input.value;
        const match = findVariant(variants, candidate);
        const label = labelFor(input);
        if (!label) return;
        if (fieldset.matches('[data-v27-size-fieldset]')) {
          input.disabled = !match || !match.available || label.hidden;
          label.setAttribute('aria-disabled', String(input.disabled));
        }

        label.classList.remove('available', 'soldout', 'unavailable');
        if (!match) label.classList.add('unavailable');
        else if (match.available) label.classList.add('available');
        else label.classList.add('soldout');
      });
    });
  }

  function bindPersonalisation(root) {
    root.querySelectorAll('[data-v27-personalisation]').forEach(panel => {
      if (panel.dataset.v27PersonalisationBound === '1') return;
      panel.dataset.v27PersonalisationBound = '1';

      const optionName = panel.dataset.v27PersonalisationOption || '';
      const hiddenValues = new Set(String(panel.dataset.v27PersonalisationHideValues || '')
        .split('|').map(normalise).filter(Boolean));
      const properties = [...panel.querySelectorAll('[data-v27-personalisation-property]')];
      const nameInput = panel.querySelector('[data-v27-personalisation-name]');
      const optionInputs = [...root.querySelectorAll('[data-v27-variant-engine] input[type="radio"]')]
        .filter(input => input.name === optionName);
      const threadInput = panel.querySelector('[data-v27-thread-input]');
      const chosen = panel.querySelector('[data-v27-thread-chosen]');
      const threadButtons = [...panel.querySelectorAll('[data-v27-thread-colour]')];

      function applyVisibility() {
        const value = normalise(optionInputs.find(input => input.checked)?.value || '');
        const hide = Boolean(optionName && value && hiddenValues.has(value));
        panel.hidden = hide;
        panel.classList.toggle('is-hidden', hide);
        panel.setAttribute('aria-hidden', hide ? 'true' : 'false');
        properties.forEach(input => {
          input.disabled = hide;
          if (hide) input.removeAttribute('required');
          else if (input.dataset.v27Required === 'true') input.required = true;
        });
        if (hide && nameInput) nameInput.value = '';
      }

      optionInputs.forEach(input => input.addEventListener('change', applyVisibility));
      threadButtons.forEach(button => button.addEventListener('click', () => {
        const colour = button.dataset.v27ThreadColour || '';
        threadButtons.forEach(item => {
          const active = item === button;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-selected', active ? 'true' : 'false');
        });
        if (threadInput) threadInput.value = colour;
        if (chosen) chosen.textContent = colour;
      }));
    });
  }

  function bindGuide(root) {
    const openButton = root.querySelector('[data-v27-size-open]');
    const drawer = document.querySelector('[data-v27-guide-drawer]');
    const overlay = document.querySelector('[data-v27-guide-overlay]');
    if (!openButton || !drawer || !overlay || drawer.dataset.v27Bound === '1') return;
    drawer.dataset.v27Bound = '1';
    /* The drawer is rendered inside the sticky product column, which creates its own stacking
       context (z-index 2). Anything fixed in there sits under the site header regardless of its own
       z-index, so the close button was unreachable on desktop. Portal both nodes to <body>. */
    if (drawer.parentElement !== document.body) document.body.appendChild(drawer);
    if (overlay.parentElement !== document.body) document.body.appendChild(overlay);

    let config = null;
    const readConfig = () => {
      if (config) return config;
      config = parseJson(document.querySelector('[data-pv2-guide-config]'), null);
      return config;
    };
    const toArray = value => String(value || '').split(',').map(item => item.trim());

    function renderGuide(key) {
      const data = readConfig();
      if (!data?.guides) return;
      if (!data.guides[key]) key = Object.keys(data.guides)[0];
      const guide = data.guides[key];
      if (!guide) return;

      const heading = drawer.querySelector('[data-v27-guide-heading]');
      const eyebrow = drawer.querySelector('[data-v27-guide-eyebrow]');
      const fit = drawer.querySelector('[data-v27-guide-fit]');
      const note = drawer.querySelector('[data-v27-guide-note]');
      if (heading) heading.textContent = data.heading || 'Size Guide';
      if (eyebrow) eyebrow.textContent = data.eyebrow || 'PRINTORA FIT GUIDE';
      if (fit) fit.textContent = guide.fit || '';
      if (note) note.textContent = guide.note || '';

      const image = drawer.querySelector('[data-v27-guide-image]');
      if (image) {
        image.src = guide.measure_image || guide.guide_image || '';
        image.hidden = !image.src;
      }

      drawer.querySelectorAll('[data-v27-guide-key]').forEach(button => {
        const exists = Boolean(data.guides[button.dataset.v27GuideKey]);
        button.hidden = !exists;
        button.classList.toggle('is-active', button.dataset.v27GuideKey === key);
      });

      const sizes = toArray(guide.sizes);
      const rows = [
        [guide.chest_label || 'Chest', toArray(guide.chest)],
        [guide.length_label || 'Length', toArray(guide.length)],
        [guide.sleeve_label || 'Sleeve', toArray(guide.sleeve)]
      ].filter(row => row[1].some(Boolean));

      const head = drawer.querySelector('[data-v27-guide-head]');
      const body = drawer.querySelector('[data-v27-guide-body]');
      if (head) {
        head.replaceChildren();
        ['Size', ...sizes].forEach(text => {
          const th = document.createElement('th');
          th.textContent = text;
          head.appendChild(th);
        });
      }
      if (body) {
        body.replaceChildren();
        rows.forEach(([label, values]) => {
          const tr = document.createElement('tr');
          [label, ...sizes.map((_, index) => values[index] || '')].forEach(text => {
            const td = document.createElement('td');
            td.textContent = text;
            tr.appendChild(td);
          });
          body.appendChild(tr);
        });
      }

      drawer.querySelectorAll('[data-v27-height]').forEach(button => {
        const height = button.dataset.v27Height;
        const label = data[`height_${height}`] || '';
        button.textContent = label;
        button.hidden = !label;
      });
      const active = drawer.querySelector('[data-v27-height].is-active')
        || drawer.querySelector('[data-v27-height="regular"]')
        || drawer.querySelector('[data-v27-height]:not([hidden])');
      if (active) active.classList.add('is-active');
      const result = drawer.querySelector('[data-v27-height-result]');
      if (active && result) result.textContent = data[`height_${active.dataset.v27Height}_text`] || '';
    }

    function preferredGuide() {
      const garment = root.querySelector('[data-v27-garment].is-active')?.dataset.v27Garment;
      if (garment === 'hoodies' || garment === 'jumpers' || garment === 'tshirts') return garment;
      const handle = normalise(root.closest('[data-product-handle]')?.dataset.productHandle || '');
      if (handle.includes('hoodie')) return 'hoodies';
      if (handle.includes('jumper') || handle.includes('sweatshirt')) return 'jumpers';
      return 'tshirts';
    }

    function openGuide() {
      renderGuide(preferredGuide());
      overlay.hidden = false;
      drawer.hidden = false;
      requestAnimationFrame(() => {
        overlay.classList.add('is-open');
        drawer.classList.add('is-open');
      });
      document.body.classList.add('pv27-guide-open');
    }

    function closeGuide() {
      overlay.classList.remove('is-open');
      drawer.classList.remove('is-open');
      document.body.classList.remove('pv27-guide-open');
      window.setTimeout(() => {
        overlay.hidden = true;
        drawer.hidden = true;
      }, 280);
    }

    openButton.addEventListener('click', openGuide);
    drawer.querySelector('[data-v27-guide-close]')?.addEventListener('click', closeGuide);
    overlay.addEventListener('click', closeGuide);
    drawer.querySelectorAll('[data-v27-guide-key]').forEach(button => {
      button.addEventListener('click', () => renderGuide(button.dataset.v27GuideKey));
    });
    drawer.querySelectorAll('[data-v27-height]').forEach(button => {
      button.addEventListener('click', () => {
        drawer.querySelectorAll('[data-v27-height]').forEach(item => item.classList.toggle('is-active', item === button));
        const data = readConfig();
        const result = drawer.querySelector('[data-v27-height-result]');
        if (data && result) result.textContent = data[`height_${button.dataset.v27Height}_text`] || '';
      });
    });
    document.addEventListener('keydown', event => {
      if (event.key === 'Escape' && !drawer.hidden) closeGuide();
    });
  }

  function bindProduct(root) {
    if (!root || root.dataset.v27Bound === '1') return;
    root.dataset.v27Bound = '1';

    const engine = root.querySelector('[data-v27-variant-engine]');
    const variants = parseJson(engine?.querySelector('[data-v27-variants]'), []);
    const productId = engine?.dataset.product || '';

    const galleryShell = root.querySelector('[data-v27-gallery-shell]');
    const gallery = root.querySelector('[data-v27-gallery]');
    const tiles = [...root.querySelectorAll('[data-v27-gallery-item]')];
    const thumbs = [...root.querySelectorAll('[data-v27-thumb]')];
    const thumbRail = root.querySelector('[data-v27-thumbs]');
    const loadMore = root.querySelector('[data-v27-gallery-load]');
    const colourButtons = [...root.querySelectorAll('[data-v27-colour]')];
    const colourInputs = [...root.querySelectorAll('[data-v27-colour-input]')];
    const colourName = root.querySelector('[data-v27-colour-name]');
    const colourCount = Number(galleryShell?.dataset.v27ColourCount || root.querySelector('[data-v27-options]')?.dataset.v27ColourCount || 0);

    const optionFields = fields(engine);
    const garmentField = optionFields.find(field => field.querySelector('input[name="Garment"]'));
    const sizeField = optionFields.find(field => field.matches('[data-v27-size-fieldset]'));
    const garmentIndex = optionFields.indexOf(garmentField);
    const sizeIndex = optionFields.indexOf(sizeField);
    const colourIndex = optionFields.findIndex(field => field.querySelector('[data-v27-colour-input]'));
    const separateGarment = Boolean(garmentField && sizeField && colourIndex >= 0);
    const isKidsSize = value => /years?|yrs?|kids/i.test(String(value));
    let garmentAudience = isKidsSize(sizeField?.querySelector('input:checked')?.value) ? 'kids' : 'adults';

    function filterSeparateGarment() {
      if (!separateGarment) return;
      const values = selectedValues(engine);
      const garment = values[garmentIndex];
      const garmentPool = variants.filter(v => v.options[garmentIndex] === garment);
      if (!garmentPool.some(v => (isKidsSize(v.options[sizeIndex]) ? 'kids' : 'adults') === garmentAudience)) garmentAudience = 'adults';
      const pool = garmentPool.filter(v => (isKidsSize(v.options[sizeIndex]) ? 'kids' : 'adults') === garmentAudience);
      root.querySelectorAll('[data-pv2-audience-selector] [data-pv2-audience]').forEach(button => {
        const audience = button.dataset.pv2Audience;
        button.hidden = !garmentPool.some(v => (isKidsSize(v.options[sizeIndex]) ? 'kids' : 'adults') === audience);
        button.classList.toggle('is-active', audience === garmentAudience);
        button.setAttribute('aria-pressed', String(audience === garmentAudience));
      });
      const audienceName = root.querySelector('[data-pv2-audience-name]');
      if (audienceName) audienceName.textContent = garmentAudience === 'kids' ? 'Kids' : 'Adults';
      let visible = 0;
      colourButtons.forEach(button => {
        const show = pool.some(v => v.options[colourIndex] === button.dataset.v27Colour);
        button.hidden = !show;
        button.classList.toggle('pv2-option-hidden', !show);
        if (show) visible++;
        button.classList.toggle('is-extra', show && visible > 5);
        button.classList.toggle('is-extra-m', show && visible > 4);
      });
      const more = root.querySelector('[data-v27-colour-more]');
      if (more) more.hidden = visible <= 4;
      const chosenColour = colourInputs.find(input => input.checked);
      if (!pool.some(v => v.options[colourIndex] === chosenColour?.value)) {
        const next = pool.find(v => v.available) || pool[0];
        const input = colourInputs.find(input => input.value === next?.options[colourIndex]);
        if (input) { input.checked = true; syncColourVisual(input.value, true); }
      }
      sizeField.querySelectorAll('input[type="radio"]').forEach(input => {
        const label = labelFor(input);
        if (!label) return;
        const show = pool.some(v => v.options[sizeIndex] === input.value);
        label.hidden = !show;
        label.classList.toggle('pv2-option-hidden', !show);
      });
      const selectedSize = sizeField.querySelector('input:checked');
      if (labelFor(selectedSize)?.hidden) {
        const next = pool.find(v => v.available && v.options[colourIndex] === colourInputs.find(i => i.checked)?.value)
          || pool.find(v => v.available) || pool[0];
        const input = [...sizeField.querySelectorAll('input')].find(i => i.value === next?.options[sizeIndex]);
        if (input) input.checked = true;
      }
    }

    let selectedMediaId = gallery?.querySelector('.is-mobile-hero')?.dataset.v27MediaId || '';
    let touchX = null;
    let touchY = null;

    const eligible = (node, token) => {
      if (!node) return false;
      if (colourCount <= 1) return true;
      if (node.dataset.v27Shared === 'true') return true;
      return String(node.dataset.v27Colours || '').includes(`|${token}|`);
    };

    const selectedColourToken = () => {
      const checked = colourInputs.find(input => input.checked);
      if (checked) return slug(checked.value);
      return colourButtons.find(button => button.classList.contains('is-active'))?.dataset.v27ColourToken || '';
    };

    const mediaRank = (node, token) => {
      const match = String(node.dataset.v27Colours || '').includes(`|${token}|`);
      return match ? 0 : node.dataset.v27Shared === 'true' ? 1 : 2;
    };
    const eligibleTiles = token => tiles.filter(tile => eligible(tile, token))
      .sort((a, b) => mediaRank(a, token) - mediaRank(b, token));

    function scrollThumbHorizontally(thumb) {
      if (!thumbRail || !thumb) return;
      const left = thumb.offsetLeft;
      const right = left + thumb.offsetWidth;
      const viewLeft = thumbRail.scrollLeft;
      const viewRight = viewLeft + thumbRail.clientWidth;
      let target = viewLeft;
      if (left < viewLeft) target = left;
      else if (right > viewRight) target = right - thumbRail.clientWidth;
      if (target !== viewLeft) thumbRail.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
    }

    function setHero(mediaId, token = selectedColourToken(), scrollThumb = false) {
      const valid = eligibleTiles(token);
      if (!valid.length) return;
      let target = valid.find(tile => String(tile.dataset.v27MediaId) === String(mediaId));
      if (!target) target = valid[0];
      selectedMediaId = target.dataset.v27MediaId || '';

      tiles.forEach(tile => {
        const active = tile === target;
        tile.classList.toggle('is-mobile-hero', active);
        if (MOBILE.matches) {
          tile.hidden = !active;
          tile.classList.toggle('is-visible', active);
        }
      });
      thumbs.forEach(thumb => {
        const active = String(thumb.dataset.v27MediaId) === String(selectedMediaId);
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-current', active ? 'true' : 'false');
        if (active && scrollThumb) scrollThumbHorizontally(thumb);
      });
    }

    function applyGallery(token, keepHero = true) {
      if (!gallery) return;
      const expanded = gallery.dataset.v27Expanded === 'true';
      let visibleIndex = 0;
      const valid = [];

      const ordered = tiles.slice().sort((a, b) => mediaRank(a, token) - mediaRank(b, token));
      ordered.forEach(tile => gallery.insertBefore(tile, loadMore || null));
      const orderedThumbs = thumbs.slice().sort((a, b) => mediaRank(a, token) - mediaRank(b, token));
      orderedThumbs.forEach(thumb => thumbRail?.appendChild(thumb));
      ordered.forEach(tile => {
        const ok = eligible(tile, token);
        tile.classList.toggle('is-colour-hidden', !ok);
        if (ok) valid.push(tile);

        if (MOBILE.matches) return;
        if (!ok) {
          tile.hidden = true;
          tile.classList.remove('is-visible');
          return;
        }
        visibleIndex += 1;
        const show = expanded || visibleIndex <= 4;
        tile.hidden = !show;
        tile.classList.toggle('is-extra', visibleIndex > 4);
        tile.classList.toggle('is-visible', show);
      });

      thumbs.forEach(thumb => {
        const ok = eligible(thumb, token);
        thumb.hidden = !ok;
        thumb.classList.toggle('is-colour-hidden', !ok);
      });

      if (MOBILE.matches) {
        setHero(keepHero ? selectedMediaId : '', token, false);
        if (loadMore) loadMore.hidden = true;
      } else {
        tiles.forEach(tile => tile.classList.remove('is-mobile-hero'));
        if (loadMore) loadMore.hidden = expanded || valid.length <= 4;
      }

      if (galleryShell) galleryShell.dataset.v27SelectedColour = token;
    }

    function syncColourVisual(value, resetGallery = true) {
      const token = slug(value);
      colourButtons.forEach(button => {
        const active = slug(button.dataset.v27Colour) === token;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
      if (colourName) colourName.textContent = value || '';
      if (resetGallery) {
        if (gallery) gallery.dataset.v27Expanded = 'false';
        applyGallery(token, false);
      }
      setFabric(root, value);
    }

    function syncVariant(updateUrl, options = {}) {
      if (!engine || !variants.length) return null;
      filterSeparateGarment();
      const values = selectedValues(engine);
      let variant = findVariant(variants, values);
      /* Only hunt for another size when a colour/fabric change stranded the current one. A size the
         customer clicked themselves must stay selected, even if it is sold out. */
      const allowFallback = options.sizeFallback !== false;
      if (allowFallback && (!variant || !variant.available)) {
        const optionFields = fields(engine);
        const sizeField = optionFields.find(fieldset => fieldset.matches('[data-v27-size-fieldset]'));
        if (sizeField) {
          const sizeIndex = optionFields.indexOf(sizeField);
          const candidate = values.slice();
          const sizeInputs = [...sizeField.querySelectorAll('input[type="radio"]')]
            .filter(sizeInput => !(labelFor(sizeInput)?.hidden));
          for (const sizeInput of sizeInputs) {
            candidate[sizeIndex] = sizeInput.value;
            const match = findVariant(variants, candidate);
            if (match && match.available) {
              sizeInput.checked = true;
              variant = match;
              break;
            }
          }
        }
      }
      updateOptionHeadings(engine);
      updateOptionStatuses(engine, variants);
      syncFabricVisual(root);
      setVariantInputs(productId, variant);
      setAddButton(root, productId, variant);
      setStock(root, variant);
      setKidsDetails(root, variant);
      if (variant) setPrice(root, productId, variant);

      if (variant && updateUrl) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', String(variant.id));
        window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
      }

      root.dispatchEvent(new CustomEvent('v27:variantchange', {
        bubbles: true,
        detail: { variant, options: selectedValues(engine) }
      }));
      return variant;
    }

    if (engine) {
      engine.addEventListener('click', event => {
        const label = event.target.closest?.('[data-v27-size-label]');
        if (!label) return;
        const input = label.htmlFor ? document.getElementById(label.htmlFor) : null;
        if (!input) return;
        event.preventDefault();
        if (label.hidden || input.disabled) return;
        if (!input.checked) {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
      engine.addEventListener('change', event => {
        const input = event.target;
        if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;

        if (input.matches('[data-v27-colour-input]')) syncColourVisual(input.value, true);
        if (input.matches('[data-v27-fabric-input]')) {
          syncFabricVisual(root);
          const moved = applyGarmentFilter(root);
          if (moved.colourChanged) {
            const colour = colourInputs.find(item => item.checked);
            if (colour) syncColourVisual(colour.value, true);
          }
          syncAudienceVisual(root);
        }
        const fromSize = Boolean(input.closest('[data-v27-size-fieldset]'));
        syncVariant(true, { sizeFallback: !fromSize });
        if (!fromSize) window.PrintoraColourFallbacks?.syncRoot(root);
      });
    }

    root.querySelectorAll('[data-v27-fabric-choice]').forEach(button => {
      button.addEventListener('click', () => {
        const value = button.dataset.v27FabricValue || '';
        const input = root.querySelector(`[data-v27-fabric-input][value="${CSS.escape ? CSS.escape(value) : value.replace(/"/g, '\\"')}"]`)
          || [...root.querySelectorAll('[data-v27-fabric-input]')].find(item => item.value === value);
        if (!input) return;
        input.checked = true;
        syncFabricVisual(root);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });

    colourButtons.forEach(button => {
      button.addEventListener('click', () => {
        const value = button.dataset.v27Colour || '';
        const input = colourInputs.find(item => item.value === value);
        if (!input) return;
        if (!input.checked) input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });

    /* Keyboard users: arrow keys move the native radio, which fires change; nothing else needed.
       The selected look comes purely from input:checked + label, so it can never get out of sync. */

    /* Adults / Kids selector for products that carry both adult and kids garments in the Fabric option. */
    const audienceSelector = root.querySelector('[data-pv2-audience-selector]');
    let lastAdultFabric = '';
    const rememberAdultFabric = () => {
      const checked = root.querySelector('[data-v27-fabric-input]:checked');
      if (checked && (checked.dataset.pv2Audience || 'adults') === 'adults') lastAdultFabric = checked.value;
    };
    rememberAdultFabric();
    if (audienceSelector) {
      audienceSelector.querySelectorAll('[data-pv2-audience]').forEach(button => {
        button.addEventListener('click', () => {
          const audience = button.dataset.pv2Audience || 'adults';
          if (separateGarment) {
            garmentAudience = audience;
            syncVariant(true);
            window.PrintoraColourFallbacks?.syncRoot(root);
            return;
          }
          rememberAdultFabric();
          const choices = [...root.querySelectorAll('[data-v27-fabric-choice]')];
          choices.forEach(choice => {
            const show = (choice.dataset.pv2Audience || 'adults') === audience;
            choice.hidden = !show;
            choice.classList.toggle('pv2-option-hidden', !show);
          });
          const checked = root.querySelector('[data-v27-fabric-input]:checked');
          const currentAudience = checked ? (checked.dataset.pv2Audience || 'adults') : '';
          if (currentAudience !== audience) {
            const pool = choices.filter(choice => !choice.hidden);
            const target = (audience === 'adults' && pool.find(choice => choice.dataset.v27FabricValue === lastAdultFabric)) || pool[0];
            if (target) {
              target.click();
              return;
            }
          }
          syncAudienceVisual(root);
        });
      });
    }

    root.querySelectorAll('[data-v27-garment]').forEach(button => {
      if (button.matches('input,[data-v27-size-label]')) return;
      if (button.closest('[data-pv2-audience-selector]')) return;
      button.addEventListener('click', () => {
        const key = button.dataset.v27Garment || '';
        const selector = button.closest('[data-v27-garment-selector]');
        if (!selector) return;

        selector.querySelectorAll('[data-v27-garment]').forEach(item => {
          const active = item === button;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
        const name = selector.querySelector('[data-v27-garment-name]');
        if (name) name.textContent = button.textContent.trim();

        const fieldset = root.querySelector('[data-v27-size-fieldset][data-v27-combined-size="true"]');
        if (!fieldset) return;
        const currentCompact = fieldset.querySelector('input:checked')?.nextElementSibling?.textContent.trim() || '';
        let candidate = null;
        fieldset.querySelectorAll('[data-v27-size-label]').forEach(label => {
          const show = label.dataset.v27Garment === key;
          label.hidden = !show;
          label.classList.toggle('pv2-option-hidden', !show);
          if (show && !candidate) candidate = label;
          if (show && label.textContent.trim() === currentCompact) candidate = label;
        });
        const input = candidate?.htmlFor ? document.getElementById(candidate.htmlFor) : null;
        if (input && !input.checked) {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    });

    if (loadMore) {
      loadMore.addEventListener('click', () => {
        gallery.dataset.v27Expanded = 'true';
        applyGallery(selectedColourToken(), true);
      });
    }

    thumbs.forEach(thumb => thumb.addEventListener('click', () => {
      setHero(thumb.dataset.v27MediaId, selectedColourToken(), true);
    }));

    function stepHero(delta) {
      const valid = eligibleTiles(selectedColourToken());
      if (valid.length < 2) return;
      let index = valid.findIndex(tile => String(tile.dataset.v27MediaId) === String(selectedMediaId));
      if (index < 0) index = 0;
      index = (index + delta + valid.length) % valid.length;
      setHero(valid[index].dataset.v27MediaId, selectedColourToken(), true);
    }

    root.querySelector('[data-v27-gallery-prev]')?.addEventListener('click', () => stepHero(-1));
    root.querySelector('[data-v27-gallery-next]')?.addEventListener('click', () => stepHero(1));

    galleryShell?.addEventListener('touchstart', event => {
      const touch = event.changedTouches?.[0];
      if (!touch || !MOBILE.matches) return;
      touchX = touch.clientX;
      touchY = touch.clientY;
    }, { passive: true });

    galleryShell?.addEventListener('touchend', event => {
      const touch = event.changedTouches?.[0];
      if (!touch || touchX === null || touchY === null || !MOBILE.matches) return;
      const dx = touch.clientX - touchX;
      const dy = touch.clientY - touchY;
      touchX = null;
      touchY = null;
      if (Math.abs(dx) < 42 || Math.abs(dx) <= Math.abs(dy) * 1.15) return;
      stepHero(dx < 0 ? 1 : -1);
    }, { passive: true });

    const lightbox = root.querySelector('[data-v27-lightbox]');
    const lightboxImage = lightbox?.querySelector('[data-v27-lightbox-image]');
    let lightboxId = '';
    let lightboxHome = null;

    function mountLightboxToBody() {
      if (!lightbox || lightbox.parentElement === document.body) return;
      lightboxHome = lightbox.parentElement;
      document.body.appendChild(lightbox);
    }

    function restoreLightboxHome() {
      if (!lightbox || !lightboxHome || lightbox.parentElement === lightboxHome) return;
      lightboxHome.appendChild(lightbox);
      lightboxHome = null;
    }

    function openLightbox(tile) {
      if (!lightbox || !lightboxImage || !tile) return;
      mountLightboxToBody();
      lightboxId = tile.dataset.v27MediaId || '';
      lightboxImage.src = tile.dataset.v27Full || tile.querySelector('img')?.currentSrc || '';
      lightboxImage.alt = tile.querySelector('img')?.alt || '';
      lightboxImage.removeAttribute('width');
      lightboxImage.removeAttribute('height');
      lightboxImage.style.width = '';
      lightboxImage.style.height = '';
      lightboxImage.style.maxWidth = '';
      lightboxImage.style.maxHeight = '';
      lightbox.hidden = false;
      lightbox.setAttribute('aria-hidden', 'false');
      document.body.classList.add('pv27-lightbox-open');
    }

    function closeLightbox() {
      if (!lightbox) return;
      lightbox.hidden = true;
      lightbox.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('pv27-lightbox-open');
      restoreLightboxHome();
    }

    function stepLightbox(delta) {
      const valid = eligibleTiles(selectedColourToken());
      if (!valid.length) return;
      let index = valid.findIndex(tile => String(tile.dataset.v27MediaId) === String(lightboxId));
      if (index < 0) index = 0;
      index = (index + delta + valid.length) % valid.length;
      openLightbox(valid[index]);
    }

    tiles.forEach(tile => tile.addEventListener('click', () => {
      if (!MOBILE.matches) openLightbox(tile);
    }));
    lightbox?.querySelector('[data-v27-lightbox-close]')?.addEventListener('click', closeLightbox);
    lightbox?.querySelector('[data-v27-lightbox-prev]')?.addEventListener('click', () => stepLightbox(-1));
    lightbox?.querySelector('[data-v27-lightbox-next]')?.addEventListener('click', () => stepLightbox(1));
    lightbox?.addEventListener('click', event => {
      if (event.target === lightbox) closeLightbox();
    });

    bindGuide(root);
    bindPersonalisation(root);

    sortSizeGrid(root);
    applyGarmentFilter(root);
    syncAudienceVisual(root);
    const initialColour = colourInputs.find(input => input.checked)?.value
      || colourButtons.find(button => button.classList.contains('is-active'))?.dataset.v27Colour
      || '';
    if (initialColour) syncColourVisual(initialColour, true);
    else applyGallery(selectedColourToken(), true);
    syncFabricVisual(root);
    syncVariant(false);

    MOBILE.addEventListener?.('change', () => applyGallery(selectedColourToken(), true));
  }

  function boot(scope = document) {
    scope.querySelectorAll?.('[data-printora-v27]').forEach(bindProduct);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot(), { once: true });
  else boot();
  document.addEventListener('shopify:section:load', event => boot(event.target || document));
})();

